import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context.js';

type Row = Record<string, unknown>;

/**
 * Planning hooks. Read-only today — backup/restore writes (POST to
 * `/v1/planning/{backups,restores}`) land in a follow-up commit
 * with their own IAM scope (Lambda-Url SQS-send permission).
 */
/**
 * Engine returns `{ data: Row[] }` for both /system-logs and /day-logs.
 * Hooks unwrap to a bare array for consumers.
 */
/**
 * Read recent system-log rows for the current license. Optional
 * `filter` narrows the feed to a single workload (e.g.
 * `planning-backup` for the Backup page's runs table); pass a
 * `refetchInterval` to keep the panel live while async workflows
 * are running.
 */
export function useSystemLogs(
  filter?: string,
  options?: { refetchIntervalMs?: number },
): {
  data?: Row[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { session, api } = useAuth();
  const path = filter
    ? `/v1/logs/system?${new URLSearchParams({ filter }).toString()}`
    : '/v1/logs/system';
  const q = useQuery({
    queryKey: ['logs', 'system', session?.licenseId ?? 'anon', filter ?? ''],
    queryFn: () => api.get<{ data: Row[] }>(path).then((r) => r.data),
    enabled: !!session,
    staleTime: 30_000,
    refetchInterval: options?.refetchIntervalMs,
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: (q.error as Error) ?? null,
    refetch: () => void q.refetch(),
  };
}

export function useDayLogs(): { data?: Row[]; isLoading: boolean; error: Error | null } {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['logs', 'day', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<{ data: Row[] }>('/v1/logs/day').then((r) => r.data),
    enabled: !!session,
    staleTime: 60_000,
  });
  return { data: q.data, isLoading: q.isLoading, error: (q.error as Error) ?? null };
}

/**
 * Trigger a planning backup. POSTs to `/v1/planning/backups`, which
 * forwards to the engine, which inserts a `backups` row + enqueues
 * the work on SQS for the Fargate consumer. Engine returns 201 with
 * the job's id + status + createdAt.
 *
 * Required body shape (engine validates):
 *   { instance, workspaceIds?: string[], recordTypeIds?: string[], name? }
 *   At least one of workspaceIds / recordTypeIds is required.
 */
export interface PlanningBackupInput {
  instance: string;
  workspaceIds?: ReadonlyArray<string>;
  recordTypeIds?: ReadonlyArray<string>;
  name?: string;
}

export function useTriggerPlanningBackup(): {
  trigger: (payload: PlanningBackupInput) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
  data: unknown;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const queryKey = ['planning', 'backups', session?.licenseId ?? 'anon'] as const;
  const m = useMutation({
    mutationFn: (payload: PlanningBackupInput) =>
      api.post<unknown>('/v1/planning/backups', payload),
    onMutate: async (payload: PlanningBackupInput) => {
      // Optimistic prepend: a skeleton row in `Queued` state appears
      // at the top of the table immediately so the operator sees
      // their click took effect. The temp id is keyed by `Date.now()`
      // so successive clicks don't collide. `onSettled` invalidates
      // the query, which refetches the canonical row from the engine
      // and replaces the optimistic one. `onError` filters the
      // skeleton out so a 4xx doesn't leave a ghost row.
      await qc.cancelQueries({ queryKey });
      const tempId = `pending-${Date.now()}`;
      const prev = qc.getQueryData<PlanningBackupJob[]>(queryKey);
      const optimistic: PlanningBackupJob = {
        id: tempId,
        licenseId: session?.licenseId ?? 'anon',
        instance: payload.instance,
        status: 'Queued',
        object: {
          workspaceIds: payload.workspaceIds,
          recordTypeIds: payload.recordTypeIds,
        },
        fileUrl: null,
        log: null,
        createdAt: new Date().toISOString(),
        startedAt: null,
        finishedAt: null,
        updatedAt: new Date().toISOString(),
      };
      qc.setQueryData<PlanningBackupJob[]>(queryKey, [optimistic, ...(prev ?? [])]);
      return { tempId, prev };
    },
    onError: (err: Error, _payload, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      toast.error(`Could not trigger backup: ${err.message}`);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: ['logs', 'system'] });
    },
    onSuccess: () => {
      toast.success('Backup queued — watch the history tab for status');
    },
  });
  return {
    trigger: (payload) => m.mutateAsync(payload),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
    data: m.data,
  };
}

/**
 * Trigger a planning restore. Engine expects:
 *   { instance, sourceBackupId (a Completed backup of this license), scope?, name? }
 */
export interface PlanningRestoreInput {
  instance: string;
  sourceBackupId: string;
  scope?: Record<string, unknown>;
  name?: string;
}

export function useTriggerPlanningRestore(): {
  trigger: (payload: PlanningRestoreInput) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
  data: unknown;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const queryKey = ['planning', 'restores', session?.licenseId ?? 'anon'] as const;
  const m = useMutation({
    mutationFn: (payload: PlanningRestoreInput) =>
      api.post<unknown>('/v1/planning/restores', payload),
    onMutate: async (payload: PlanningRestoreInput) => {
      // Same optimistic-prepend pattern as the backup trigger above —
      // skeleton `Queued` row appears at the top of the Restores tab
      // immediately, the polling tightens to 5 s because `Queued`
      // counts as in-flight, and the next refetch replaces the
      // skeleton with the canonical row from the engine.
      await qc.cancelQueries({ queryKey });
      const tempId = `pending-${Date.now()}`;
      const prev = qc.getQueryData<PlanningRestoreJob[]>(queryKey);
      const optimistic: PlanningRestoreJob = {
        id: tempId,
        licenseId: session?.licenseId ?? 'anon',
        instance: payload.instance,
        status: 'Queued',
        scope: payload.scope ?? {},
        // The engine copies the backup's archive location onto the row; the
        // next refetch fills it in.
        sourceUrl: '',
        log: null,
        createdAt: new Date().toISOString(),
        startedAt: null,
        finishedAt: null,
        updatedAt: new Date().toISOString(),
      };
      qc.setQueryData<PlanningRestoreJob[]>(queryKey, [optimistic, ...(prev ?? [])]);
      return { tempId, prev };
    },
    onError: (err: Error, _payload, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      toast.error(`Could not trigger restore: ${err.message}`);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: ['logs', 'system'] });
    },
    onSuccess: () => {
      toast.success('Restore queued — watch the history tab for status');
    },
  });
  return {
    trigger: (payload) => m.mutateAsync(payload),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
    data: m.data,
  };
}

/**
 * Delete a planning-backup row. The engine drops the `backups` row
 * (tenant-scoped on license_id from the JWT) and best-effort removes
 * the S3 archive that `file_url` pointed at. 404 surfaces as a
 * mutation error — caller's `onError` toasts.
 *
 * Optimistic update: flips the row's status to `'Deleting'` the
 * moment the mutation starts. The host page treats `'Deleting'` as
 * an in-flight status, so polling tightens to 5 s and the row
 * disappears on the next refetch once the engine commits the DELETE
 * + S3 cleanup. Bridges the eventual-consistency gap between
 * "engine returned 200" and "list refetch sees the row gone".
 * On error, the snapshot is rolled back so the row reappears with
 * its prior status.
 */
export function useDeletePlanningBackup(): {
  remove: (backupId: string) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const queryKey = ['planning', 'backups', session?.licenseId ?? 'anon'] as const;
  const m = useMutation({
    mutationFn: (backupId: string) =>
      api.delete<unknown>(`/v1/planning/backups/${encodeURIComponent(backupId)}`),
    onMutate: async (backupId: string) => {
      // Pause in-flight polls so the optimistic write isn't immediately
      // overwritten by a stale server response.
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<PlanningBackupJob[]>(queryKey);
      if (prev) {
        qc.setQueryData<PlanningBackupJob[]>(
          queryKey,
          prev.map((r) => (r.id === backupId ? { ...r, status: 'Deleting' } : r)),
        );
      }
      return { prev };
    },
    onError: (err: Error, _backupId, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      toast.error(`Could not remove backup: ${err.message}`);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
    onSuccess: () => {
      toast.success('Backup removed');
    },
  });
  return {
    remove: (backupId) => m.mutateAsync(backupId),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

/**
 * Delete a planning-restore row. Restore history is metadata only —
 * no S3 cleanup, just the row drop (the source backup archive lives
 * on its own row's `file_url`).
 *
 * Same optimistic-`Deleting`-status pattern as the backup hook.
 */
export function useDeletePlanningRestore(): {
  remove: (restoreId: string) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const queryKey = ['planning', 'restores', session?.licenseId ?? 'anon'] as const;
  const m = useMutation({
    mutationFn: (restoreId: string) =>
      api.delete<unknown>(`/v1/planning/restores/${encodeURIComponent(restoreId)}`),
    onMutate: async (restoreId: string) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<PlanningRestoreJob[]>(queryKey);
      if (prev) {
        qc.setQueryData<PlanningRestoreJob[]>(
          queryKey,
          prev.map((r) => (r.id === restoreId ? { ...r, status: 'Deleting' } : r)),
        );
      }
      return { prev };
    },
    onError: (err: Error, _restoreId, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      toast.error(`Could not remove restore: ${err.message}`);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
    onSuccess: () => {
      toast.success('Restore removed');
    },
  });
  return {
    remove: (restoreId) => m.mutateAsync(restoreId),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

/* ── Job-history reads ── */

export interface PlanningBackupJob {
  id: string;
  licenseId: string;
  instance: string;
  status: string;
  object: unknown;
  fileUrl: string | null;
  log: unknown;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
}

export interface PlanningRestoreJob {
  id: string;
  licenseId: string;
  instance: string;
  status: string;
  scope: unknown;
  sourceUrl: string;
  log: unknown;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
}

/**
 * Poll-friendly read of backup / restore history. Engine returns up
 * to 100 most-recent rows for the license (DESC by created_at).
 * `refetchIntervalMs` is parametrised so the host page can tighten
 * polling while a job is in flight and relax it once it settles.
 */
export function useBackupJobs(options?: { refetchIntervalMs?: number }): {
  data?: PlanningBackupJob[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['planning', 'backups', session?.licenseId ?? 'anon'],
    queryFn: () =>
      api.get<{ data: PlanningBackupJob[] }>('/v1/planning/backups').then((r) => r.data),
    enabled: !!session,
    staleTime: 30_000,
    refetchInterval: options?.refetchIntervalMs,
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: (q.error as Error) ?? null,
    refetch: () => void q.refetch(),
  };
}

export function useRestoreJobs(options?: { refetchIntervalMs?: number }): {
  data?: PlanningRestoreJob[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { session, api } = useAuth();
  const q = useQuery({
    queryKey: ['planning', 'restores', session?.licenseId ?? 'anon'],
    queryFn: () =>
      api.get<{ data: PlanningRestoreJob[] }>('/v1/planning/restores').then((r) => r.data),
    enabled: !!session,
    staleTime: 30_000,
    refetchInterval: options?.refetchIntervalMs,
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: (q.error as Error) ?? null,
    refetch: () => void q.refetch(),
  };
}
